import { nanoid } from "nanoid";
import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuid } from "uuid";

import { ApiError } from "@touchtech/web-api-client";

import {
  MediaVariants,
  NEXT_PAGE_SIZE,
  PAGE_SIZE,
  ROUTES,
} from "@is/constants";
import { ScannerContext, ViewStateContext } from "@is/contexts";
import { useSearchProducts } from "@is/hooks";
import { TrackingEventTypes, trackEvent } from "@is/services/EventTracking";
import { CacheEntryPriority } from "@is/services/MemoryCache";
import {
  ContentItem,
  ContentItemType,
  ProductContentItem,
  ProductItem,
  SearchFunctionProperties,
  SearchSizesAndLengths,
  SearchTerm,
  SearchTermType,
  Section,
  WidgetType,
} from "@is/types";
import {
  getContentPreloadMedia,
  getImageUri,
  getProductRows,
  searchForScannedSection,
  sendErrorMessageToSlack,
} from "@is/utils";
import { getEnvValue } from "@is/utils/env";

const FIRST_PAGE = 1;
const SECOND_PAGE = 2;
const THIRD_PAGE = 3;

export const useContentItems = (
  controllerRef?: React.MutableRefObject<AbortController | null>
) => {
  const navigate = useNavigate();

  const {
    items,
    updateViewState,
    suggestions,
    totalCount,
    searchTerms,
    showSearchAndFooterDrawers,
    sections,
    intersectedItemsIds,
    focusedItemId,
    hasNextPage,
  } = useContext(ViewStateContext).content;
  const { scannedValue, resetScannedValue } = useContext(ScannerContext);
  const [{ isPending, error: errorSearchingProducts }, searchProducts] =
    useSearchProducts(controllerRef);
  const [searchTermsUsed, setSearchTermsUsed] = useState([]);
  const [pendingSectionId, setPendingSectionId] = useState<string | undefined>(
    undefined
  );

  const getSearchSizesAndLengths = useCallback((searchTerms: SearchTerm[]) => {
    const sizes = [];
    const lengths = [];

    searchTerms.forEach((term) => {
      if (term.type === SearchTermType.Size) {
        sizes.push(term.value);
      } else if (term.type === SearchTermType.Length) {
        lengths.push(term.value);
      }
    });

    const searchSizesAndLengths =
      sizes.length || lengths.length
        ? {
            sizes,
            lengths,
          }
        : undefined;

    return searchSizesAndLengths;
  }, []);

  const convertSearchTermsToPreselectedSize = useCallback(
    ({
      items,
      searchSizesAndLengths,
      contentItemId,
      colorVariantId,
    }: {
      items: ContentItem[];
      searchSizesAndLengths: SearchSizesAndLengths;
      contentItemId?: string;
      colorVariantId?: string;
    }) => {
      const sizes = searchSizesAndLengths?.sizes || [];
      const lengths = searchSizesAndLengths?.lengths || [];

      const contentItemIndex = items.findIndex(
        (item) => item.id === contentItemId
      );

      if ((!sizes.length && !lengths.length) || contentItemIndex === -1) {
        return undefined;
      }

      const contentItem = items[contentItemIndex];
      let style;
      if (contentItem.type === ContentItemType.ProductRow) {
        style = contentItem.content.find((item) => item.id === colorVariantId);
      } else if (contentItem.type === ContentItemType.ProductDetailsItem) {
        style = contentItem.content;
      } else if (
        contentItem.type === ContentItemType.ContentWidgetItem &&
        contentItem.content.type !== WidgetType.HowToWearIt
      ) {
        style = contentItem.content.items.find(
          (item) => item.id === colorVariantId
        );
      }

      if (!style) {
        return undefined;
      }

      const sizeInfos =
        style.id === colorVariantId
          ? style.sizeInfos
          : style.otherVariants?.find(({ id }) => id === colorVariantId)
              ?.sizeInfos;

      if (!sizeInfos) {
        return undefined;
      }

      const isLengthRequired = sizeInfos.some((sizeInfo) => !!sizeInfo.length);

      const matchedSizes = sizes.filter((size) =>
        sizeInfos.some((info) => info.size === size)
      );
      const matchedLengths = lengths.filter((length) =>
        sizeInfos.some((info) => info.length === length)
      );

      if (matchedSizes.length !== 1) {
        return undefined;
      }

      let selectedSizeInfo;
      if (matchedSizes.length === 1 && matchedLengths.length === 1) {
        selectedSizeInfo = sizeInfos.find(
          (sizeInfo) =>
            sizeInfo.size === matchedSizes[0] &&
            sizeInfo.length === matchedLengths[0]
        );
      } else if (!isLengthRequired && matchedSizes.length === 1) {
        selectedSizeInfo = sizeInfos.find(
          (sizeInfo) => sizeInfo.size === matchedSizes[0]
        );
      }

      const ean = selectedSizeInfo?.ean || "";

      return {
        size: matchedSizes[0],
        length: matchedLengths?.[0],
        ean,
      };
    },
    []
  );

  const handleSearchProducts = useCallback(
    async ({
      newSearchTerms,
      pageNumber = 1,
      sectionId,
      enableFocusedItem = true,
      showDrawers = true,
      newSections = null,
    }: SearchFunctionProperties) => {
      if (!newSearchTerms?.length) {
        return;
      }
      setPendingSectionId(sectionId);

      // if the size of the first page is larger than the size of the next page,
      // we need to send request with a page number 1 larger. by default firstPage - 40, nextPages - 20
      // For example, first returns from 0 to 40, for the second query it returns from 20 to 40
      // (so the second request gets the data that was already returned by the first request
      const newPageNumber =
        pageNumber === SECOND_PAGE ? THIRD_PAGE : pageNumber;
      const currentSections = newSections ?? sections;

      try {
        const filteredSections = currentSections.map((section) => ({
          ...section,
          items: section.items.filter(
            (item) => intersectedItemsIds.indexOf(item.id) !== -1
          ),
          notViewedItems: section.items.filter(
            (item) => intersectedItemsIds.indexOf(item.id) === -1
          ),
        }));

        if (!sectionId) {
          // only when new search is performed
          updateViewState({ sections: filteredSections });
        }

        setSearchTermsUsed(newSearchTerms);

        const result = await searchProducts({
          terms: newSearchTerms,
          page: newPageNumber,
          pageSize: newPageNumber === FIRST_PAGE ? PAGE_SIZE : NEXT_PAGE_SIZE,
        });

        trackEvent({
          eventType: TrackingEventTypes.StylesSearch,
          data: {
            terms: newSearchTerms,
            result: !!result?.items?.length,
          },
        });

        if (result?.items?.length) {
          let newContentItems = getProductRows(
            result.items,
            currentSections,
            sectionId,
            newSearchTerms
          );

          const onlyOneItem = result.totalCount === 1;

          if (onlyOneItem) {
            // If there is only one item in the search result,
            // we should open it as PDP with possible preselected size
            newContentItems = result.items.map((item) => ({
              type: ContentItemType.ProductDetailsItem,
              id: nanoid(),
              content: item,
            }));

            const contentItem = newContentItems[0] as ProductContentItem;

            const searchSizesAndLengths =
              getSearchSizesAndLengths(newSearchTerms);

            let preselectedSize = convertSearchTermsToPreselectedSize({
              items: newContentItems,
              searchSizesAndLengths,
              contentItemId: contentItem.id,
              colorVariantId: contentItem.content.id,
            });

            if (
              !preselectedSize &&
              contentItem.content.sizeInfos?.length === 1
            ) {
              preselectedSize = {
                size: contentItem.content.sizeInfos[0].size ?? "",
                length: contentItem.content.sizeInfos[0].length ?? "",
                ean: contentItem.content.sizeInfos[0].ean ?? "",
              };
            }

            newContentItems = [
              {
                type: ContentItemType.ProductDetailsItem,
                id: contentItem.id,
                content: {
                  ...contentItem.content,
                  isPDPOpen: true,
                  isWidgetsOpen: true,
                  ...(preselectedSize && { preselectedSize }),
                  ...(searchSizesAndLengths && { searchSizesAndLengths }),
                },
              },
            ];
          }

          const preloadMediaItems = getContentPreloadMedia(result.items);

          const newSections = sectionId
            ? currentSections.map((section) => {
                const matchedSectionId = section.id === sectionId;

                let newSection = section;

                if (matchedSectionId) {
                  const isLastItemProductRow =
                    section.items[section.items.length - 1].type ===
                    ContentItemType.ProductRow;

                  const newItems = isLastItemProductRow
                    ? [...section.items.slice(0, -1), ...newContentItems]
                    : [...section.items, ...newContentItems];

                  newSection = {
                    ...section,
                    items: newItems,
                    page: result.page,
                    totalCount: result.totalCount,
                    hasNextPage: result.hasNextPage,
                  };
                }

                return newSection;
              })
            : [
                ...filteredSections,
                {
                  searchTerms: newSearchTerms,
                  items: newContentItems,
                  id: uuid(),
                  page: result.page,
                  totalCount: result.totalCount,
                  hasNextPage: result.hasNextPage,
                  notViewedItems: [],
                },
              ];

          updateViewState({
            page: result?.page,
            items: [...items, ...newContentItems],
            totalCount: result?.totalCount,
            hasNextPage: result?.hasNextPage,
            focusedItemId: enableFocusedItem
              ? newContentItems[0].id
              : focusedItemId,
            preloadMediaItems,
            suggestions: result.suggestions || [],
            sections: newSections,
            showSearchAndFooterDrawers: showDrawers && !onlyOneItem,
          });
        } else if (result?.items?.length === 0) {
          const filteredSections = currentSections.map((section) => ({
            ...section,
            items: section.items.filter(
              (item) => intersectedItemsIds.indexOf(item.id) !== -1
            ),
          }));

          updateViewState({
            sections: [
              ...filteredSections,
              {
                searchTerms: newSearchTerms,
                items: [],
                id: uuid(),
                page: result.page,
                totalCount: result.totalCount,
                notViewedItems: [],
                hasNextPage: result.hasNextPage,
              },
            ],
          });
        }

        setPendingSectionId(undefined);
      } catch (err) {
        //
      }
    },
    [
      getSearchSizesAndLengths,
      convertSearchTermsToPreselectedSize,
      searchProducts,
      sections,
      updateViewState,
      items,
      intersectedItemsIds,
      focusedItemId,
    ]
  );

  const getFocusedItemIdIndex = useCallback(
    (currentSection: Section, parentItemId: string) => {
      const lastIntersectedItem = currentSection.items
        .slice()
        .reverse()
        .find((item) => intersectedItemsIds.indexOf(item.id) !== -1);

      const focusedItemIdIndex = parentItemId
        ? currentSection.items.findIndex((item) => item.id === parentItemId)
        : currentSection.items.findIndex(
            (item) => item.id === lastIntersectedItem?.id
          );

      return focusedItemIdIndex;
    },
    [intersectedItemsIds]
  );

  const handleOpenProductDetails = useCallback(
    async ({
      productItem,
      contentItemId,
      colorVariantId,
      updatedSearchTerms,
      sectionId,
      type,
      parentItemId,
    }: {
      productItem: ProductItem;
      contentItemId?: string;
      colorVariantId?: string;
      updatedSearchTerms?: SearchTerm[];
      sectionId?: string;
      type?: ContentItemType;
      parentItemId?: string;
    }) => {
      const newContentItems = [...items];

      let newSections = [...sections];
      let currentSectionId = sectionId;

      if (!sectionId) {
        if (scannedValue) {
          const { section, isNew } = searchForScannedSection(
            newSections,
            scannedValue
          );

          if (isNew) {
            newSections = newSections.map((section) => ({
              ...section,
              items: section.items.filter(
                (item) => intersectedItemsIds.indexOf(item.id) !== -1
              ),
              notViewedItems: section.items.filter(
                (item) => intersectedItemsIds.indexOf(item.id) === -1
              ),
            }));

            newSections.push(section);
          }

          currentSectionId = section.id;
        } else {
          if (
            type === ContentItemType.WidgetItem ||
            type === ContentItemType.ContentWidgetItem
          ) {
            currentSectionId = parentItemId
              ? newSections.find((section) =>
                  section.items.find((item) => item.id === parentItemId)
                )?.id
              : newSections[newSections.length - 1]?.id;
          } else {
            currentSectionId = uuid();

            const newSection = {
              id: currentSectionId,
              items: [],
              searchTerms: [],
              page: FIRST_PAGE,
              totalCount: 10,
              notViewedItems: [],
              hasNextPage: false,
            };

            newSections.push(newSection);
          }
        }
      }

      let newSuggestions = [...suggestions];
      let newTotalCount = totalCount;
      let newHasNextPage = hasNextPage;

      const variantId = colorVariantId ? colorVariantId : productItem.id;

      const currentSection = newSections.find(
        (section) => section.id === currentSectionId
      );

      const searchSizesAndLengths = getSearchSizesAndLengths(searchTerms);
      let preselectedSize = productItem.preselectedSize
        ? productItem.preselectedSize
        : convertSearchTermsToPreselectedSize({
            items: currentSection.items,
            searchSizesAndLengths,
            contentItemId,
            colorVariantId: variantId,
          });

      if (!preselectedSize && productItem.sizeInfos?.length === 1) {
        preselectedSize = {
          size: productItem.sizeInfos[0].size ?? "",
          length: productItem.sizeInfos[0].length ?? "",
          ean: productItem.sizeInfos[0].ean ?? "",
        };
      }

      let contentItem = null;

      if (contentItemId) {
        const contentItemIndexInSection = currentSection.items.findIndex(
          (item) => item.id === contentItemId
        );

        if (contentItemIndexInSection !== -1) {
          contentItem = {
            type: ContentItemType.ProductDetailsItem,
            id: nanoid(),
            content: {
              ...productItem,
              id: variantId,
              ...(preselectedSize && { preselectedSize }),
              ...(searchSizesAndLengths && { searchSizesAndLengths }),
              isPDPOpen: true,
              isWidgetsOpen: true,
            },
          };

          currentSection.items = [
            ...currentSection.items.slice(0, contentItemIndexInSection + 1),
            contentItem,
            ...currentSection.items.slice(contentItemIndexInSection + 1),
          ];
        }
      } else {
        contentItem = {
          type: ContentItemType.ProductDetailsItem,
          id: nanoid(),
          content: {
            ...productItem,
            id: variantId,
            ...(preselectedSize && { preselectedSize }),
            isPDPOpen: true,
            isWidgetsOpen: true,
          },
        };

        const focusedItemIdIndex = getFocusedItemIdIndex(
          currentSection,
          parentItemId
        );

        currentSection.items.splice(focusedItemIdIndex + 1, 0, contentItem);
      }

      const preloadMediaItems = [];

      productItem.otherVariants?.forEach((colorVariant) => {
        colorVariant.media?.forEach((media) => {
          preloadMediaItems.push({
            url: getImageUri(
              getEnvValue("VITE_IMAGE_SERVICE_URL"),
              media.url,
              MediaVariants.MEDIUM
            ),
            cachePriority: CacheEntryPriority.NORMAL,
          });
        });
      });

      if (updatedSearchTerms) {
        const searchResult = await searchProducts({
          terms: updatedSearchTerms,
          page: FIRST_PAGE,
          pageSize: PAGE_SIZE,
        });

        if (searchResult) {
          newSuggestions = searchResult.suggestions;
          newTotalCount = searchResult.totalCount;
          newHasNextPage = searchResult.hasNextPage;
        }
      }

      if (showSearchAndFooterDrawers) {
        trackEvent({
          eventType: TrackingEventTypes.SearchDrawerClose,
          data: {},
        });
      }

      navigate(ROUTES.ContentView);

      updateViewState({
        items: newContentItems,
        focusedItemId: contentItem.id,
        showBasketDrawer: false,
        suggestions: newSuggestions,
        totalCount: newTotalCount,
        preloadMediaItems,
        lastScannedValue: scannedValue,
        showSearchAndFooterDrawers: false,
        sections: newSections,
        showSettingsDrawer: false,
        hasNextPage: newHasNextPage,
      });

      resetScannedValue();
    },
    [
      items,
      sections,
      suggestions,
      totalCount,
      hasNextPage,
      getSearchSizesAndLengths,
      searchTerms,
      convertSearchTermsToPreselectedSize,
      showSearchAndFooterDrawers,
      navigate,
      updateViewState,
      scannedValue,
      resetScannedValue,
      intersectedItemsIds,
      getFocusedItemIdIndex,
      searchProducts,
    ]
  );

  const openItemFromEntryView = useCallback(
    async (productItem: ProductItem) => {
      const newContentItems = [...items];

      const currentSectionId = uuid();
      const newSection = {
        id: currentSectionId,
        items: [],
        searchTerms: [
          {
            type: SearchTermType.Text,
            value: productItem.id,
            key: productItem.id,
            label: productItem.id,
          },
        ],
        page: FIRST_PAGE,
        totalCount: 1,
        notViewedItems: [],
        hasNextPage: false,
      };

      const contentItem: ProductContentItem = {
        type: ContentItemType.ProductDetailsItem,
        id: nanoid(),
        content: {
          ...productItem,
          isPDPOpen: true,
          isWidgetsOpen: true,
        },
      };

      newSection.items.push(contentItem);

      const preloadMediaItems = [];
      productItem.otherVariants?.forEach((colorVariant) => {
        colorVariant.media?.forEach((media) => {
          preloadMediaItems.push({
            url: getImageUri(
              getEnvValue("VITE_IMAGE_SERVICE_URL"),
              media.url,
              MediaVariants.MEDIUM
            ),
            cachePriority: CacheEntryPriority.NORMAL,
          });
        });
      });

      navigate(ROUTES.ContentView);

      updateViewState({
        items: [...newContentItems, contentItem],
        focusedItemId: contentItem.id,
        showBasketDrawer: false,
        preloadMediaItems,
        lastScannedValue: scannedValue,
        showSearchAndFooterDrawers: true,
        sections: [newSection],
        showSettingsDrawer: false,
        hasNextPage: false,
      });

      resetScannedValue();
    },
    [items, navigate, updateViewState, scannedValue, resetScannedValue]
  );

  useEffect(() => {
    if (errorSearchingProducts) {
      const message = JSON.stringify({
        searchTerms: searchTermsUsed,
        error: (errorSearchingProducts as ApiError).body,
      });

      sendErrorMessageToSlack(message);
    }
  }, [errorSearchingProducts, searchTermsUsed]);

  return {
    items,
    handleSearchProducts,
    handleOpenProductDetails,
    openItemFromEntryView,
    isPending,
    pendingSectionId,
    errorSearchingProducts,
  };
};
